import { useCallback, useEffect, useRef, useState } from "react";
import { api, API_BASE, detailText } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, Download, FileText, Loader2, FileX } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/context/LanguageContext";

const MAX_FILE_MB = 8;

export default function Documents() {
  const { t } = useT();
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, done: 0, uploaded: 0 });
  const [categories, setCategories] = useState(["Umum"]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Umum", notes: "" });
  const fileInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/documents");
      setDocs(data.documents || []);
      setSummary(data.summary || { total: 0, done: 0, uploaded: 0 });
      setCategories(data.categories || ["Umum"]);
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("docs.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) return toast.error(t("docs.name_required"));
    setAdding(true);
    try {
      await api.post("/documents", form);
      setForm({ name: "", category: form.category, notes: "" });
      toast.success(t("docs.added"));
      load();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("docs.add_error"));
    } finally {
      setAdding(false);
    }
  };

  const toggleDone = async (d) => {
    try {
      await api.patch(`/documents/${d.doc_id}`, { done: !d.done });
      setDocs((prev) => prev.map((x) => (x.doc_id === d.doc_id ? { ...x, done: !d.done } : x)));
      setSummary((s) => ({ ...s, done: s.done + (!d.done ? 1 : -1) }));
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("docs.update_error"));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(t("docs.confirm_delete"))) return;
    // Optimistic removal: baris hilang seketika tanpa load() yang memunculkan
    // spinner penuh halaman. Kalau API menolak, state dikembalikan.
    const prevDocs = docs;
    const prevSummary = summary;
    setDocs((cur) => cur.filter((x) => x.doc_id !== d.doc_id));
    setSummary((s) => ({
      ...s,
      total: Math.max(0, s.total - 1),
      uploaded: d.has_file ? Math.max(0, s.uploaded - 1) : s.uploaded,
    }));
    try {
      await api.delete(`/documents/${d.doc_id}`);
      toast.success(t("docs.deleted"));
    } catch (e) {
      setDocs(prevDocs);
      setSummary(prevSummary);
      toast.error(detailText(e?.response?.data?.detail) || t("docs.delete_error"));
    }
  };

  const pickFile = (docId) => {
    fileInputs.current[docId]?.click();
  };

  const uploadFile = async (d, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_FILE_MB * 1024 * 1024) return toast.error(t("docs.file_too_large"));
    setUploadingId(d.doc_id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/${d.doc_id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("docs.uploaded"));
      load();
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || t("docs.upload_error"));
    } finally {
      setUploadingId(null);
    }
  };

  const removeFile = async (d) => {
    try {
      await api.delete(`/documents/${d.doc_id}/file`);
      toast.success(t("docs.file_removed"));
      load();
    } catch (e) {
      toast.error(detailText(e?.response?.data?.detail) || t("docs.update_error"));
    }
  };

  // Kelompokkan per kategori agar rapi
  const grouped = docs.reduce((acc, d) => {
    const cat = d.category || "Umum";
    (acc[cat] = acc[cat] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">{t("docs.section")}</div>
        <h1 className="font-serif text-4xl">{t("docs.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("docs.subtitle")}</p>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="p-5 rounded-2xl"><div className="text-xs uppercase tracking-widest text-muted-foreground">{t("docs.stat_total")}</div><div data-testid="documents-total" className="font-serif text-2xl mt-2">{summary.total}</div></Card>
        <Card className="p-5 rounded-2xl"><div className="text-xs uppercase tracking-widest text-muted-foreground">{t("docs.stat_done")}</div><div data-testid="documents-done" className="font-serif text-2xl mt-2 text-primary">{summary.done}</div></Card>
        <Card className="p-5 rounded-2xl"><div className="text-xs uppercase tracking-widest text-muted-foreground">{t("docs.stat_uploaded")}</div><div data-testid="documents-uploaded" className="font-serif text-2xl mt-2">{summary.uploaded}</div></Card>
      </div>

      {/* Form tambah dokumen */}
      <Card className="mb-6 p-4 rounded-2xl bg-muted/30 border border-primary/20">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">{t("docs.add_title")}</Label>
        <div className="mt-2 grid md:grid-cols-[1fr_160px_1fr_auto] gap-3 items-end">
          <div>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("docs.name_ph")} data-testid="documents-name-input" />
          </div>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="documents-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <div>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={`${t("common.notes")} (${t("docs.optional")})`} />
          </div>
          <Button onClick={add} disabled={adding} className="rounded-full" data-testid="documents-add-btn">
            {adding ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            {t("docs.add_btn")}
          </Button>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">{t("docs.hint")}</div>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
      ) : docs.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground rounded-2xl">{t("docs.empty")}</Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <h2 className="font-serif text-xl mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> {cat}</h2>
              <Card className="rounded-2xl overflow-hidden divide-y divide-border">
                {items.map((d) => (
                  <div key={d.doc_id} data-testid="documents-row-item" className={`p-4 flex flex-wrap items-center gap-3 ${d.done ? "opacity-60" : ""}`}>
                    <input
                      type="checkbox"
                      checked={!!d.done}
                      onChange={() => toggleDone(d)}
                      aria-label={t("docs.mark_done")}
                      className="h-4 w-4 accent-primary shrink-0"
                    />
                    <div className="flex-1 min-w-[180px]">
                      <div className={`font-medium ${d.done ? "line-through text-muted-foreground" : ""}`}>{d.name}</div>
                      {d.notes && <div className="text-xs text-muted-foreground mt-0.5">{d.notes}</div>}
                      {!d.notes && !d.has_file && <div className="text-[11px] text-muted-foreground/70 mt-0.5 italic">{t("docs.no_file_hint")}</div>}
                    </div>

                    {/* Status & aksi file */}
                    {d.has_file && d.file_name ? (
                      <div className="flex items-center gap-2">
                        <a
                          href={`${API_BASE}/documents/${d.doc_id}/file`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid="documents-file-link"
                          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-border bg-card hover:bg-accent max-w-[220px]"
                          title={d.file_name}
                        >
                          <Download className="h-3.5 w-3.5 shrink-0 text-primary" />
                          <span className="truncate">{d.file_name}</span>
                        </a>
                        <Button variant="ghost" size="icon" onClick={() => removeFile(d)} title={t("docs.remove_file")} className="h-8 w-8 hover:bg-destructive/10 hover:text-destructive">
                          <FileX className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : null}

                    <input
                      ref={(el) => (fileInputs.current[d.doc_id] = el)}
                      type="file"
                      hidden
                      accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                      onChange={(e) => uploadFile(d, e)}
                      data-testid="documents-file-input"
                    />
                    <Button variant="outline" size="sm" onClick={() => pickFile(d.doc_id)} disabled={uploadingId === d.doc_id} className="rounded-full shrink-0" data-testid="documents-upload-btn">
                      {uploadingId === d.doc_id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                      {d.has_file ? t("docs.replace_file") : t("docs.upload")}
                    </Button>

                    <Button variant="ghost" size="icon" onClick={() => remove(d)} className="hover:bg-destructive/10 hover:text-destructive shrink-0" title={t("common.delete")} data-testid="documents-delete-btn">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
